import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Mail, Phone, MapPin, IdCard, ShieldCheck, Clock, Filter } from "lucide-react";
import PortalAccessActions from "@/components/PortalAccessActions";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const API_ROOT = process.env.REACT_APP_BACKEND_URL;
const absDoc = (u) => (u && u.startsWith("/") ? `${API_ROOT}${u}` : u);

/**
 * EpApplicationsPage — HQ review queue for prospective Distributing Partners.
 * Renders each application card with the applicant's driver's license and
 * proof-of-insurance thumbnails. Approve promotes them to an active DP;
 * Reject captures a reason and emails the applicant via Resend.
 */
export default function EpApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [approvedPortal, setApprovedPortal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/ep-applications${q}`);
      setApps(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const approve = async (app) => {
    if (!window.confirm(`Approve ${app.name}?\n\nThis creates their DP profile and emails them a welcome message.`)) return;
    setBusyId(app.id);
    try {
      const { data } = await api.post(`/ep-applications/${app.id}/approve`);
      setApprovedPortal({ name: app.name, path: data.sub_portal_path });
      toast.success(`${app.name} approved · sub-portal is live`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Approval failed");
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (app) => {
    setRejectingId(app.id);
    setRejectReason("");
  };
  const cancelReject = () => {
    setRejectingId(null);
    setRejectReason("");
  };
  const confirmReject = async (app) => {
    setBusyId(app.id);
    try {
      await api.post(`/ep-applications/${app.id}/reject`, { reason: rejectReason.trim() });
      toast.success(`${app.name} rejected · notification email sent`);
      cancelReject();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Rejection failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ep-applications-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">HQ Review</p>
          <h1 className="text-3xl font-bold">DP Applications</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review prospective Distributing Partners. Approving creates their active DP profile; rejecting emails them a reason.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-500" />
          {["pending", "approved", "rejected", "all"].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              className={filter === f ? "btn-brand" : ""}
              onClick={() => setFilter(f)}
              data-testid={`ep-apps-filter-${f}`}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>
      {approvedPortal?.path && (
        <Card className="border-emerald-300 bg-emerald-50/50 p-5" data-testid="dp-approval-completion-card">
          <p className="label-eyebrow text-emerald-800">Onboarding complete</p>
          <h2 className="mt-1 text-lg font-bold">{approvedPortal.name}'s Client sub-portal is live</h2>
          <p className="mt-1 text-xs text-gray-600">Share the direct link or downloadable QR code with the approved Distributing Partner.</p>
          <div className="mt-3"><PortalAccessActions portalUrl={`${window.location.origin}${approvedPortal.path}`} name={approvedPortal.name} testPrefix="dp-approval-portal" /></div>
        </Card>
      )}

      {loading ? (
        <p className="text-gray-400 text-sm py-10 text-center">Loading applications…</p>
      ) : apps.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-500" data-testid="ep-apps-empty">
          <Clock size={20} className="inline-block mb-2 text-gray-400" />
          <p>No {filter === "all" ? "" : filter} applications right now.</p>
        </Card>
      ) : (
        <><div className="flex items-center gap-2 text-sm text-gray-600"><SelectAllCheckbox records={apps} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /> Select all visible</div><div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {apps.map((app) => (
            <Card key={app.id} className="p-5" data-testid={`ep-app-card-${app.id}`}>
              <div className="flex justify-end"><SelectionCheckbox checked={selectedIds.includes(app.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, app.id] : selectedIds.filter((id) => id !== app.id))} testId={`select-row-checkbox-${app.id}`} label={`Select ${app.name}`} /></div>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold capitalize truncate">{app.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Applied {app.created_at ? app.created_at.slice(0, 10) : "—"}
                  </p>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${STATUS_STYLE[app.status] || STATUS_STYLE.pending}`}
                  data-testid={`ep-app-status-${app.id}`}
                >
                  {app.status}
                </span>
              </div>

              <div className="text-sm space-y-1.5 mb-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail size={12} className="shrink-0 text-gray-400" />
                  <span className="truncate">{app.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone size={12} className="shrink-0 text-gray-400" />
                  <span>{app.phone}</span>
                </div>
                <div className="flex items-start gap-2 text-gray-700">
                  <MapPin size={12} className="shrink-0 mt-1 text-gray-400" />
                  <span className="leading-snug">{app.address}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2">
                  <p className="label-eyebrow text-amber-800 flex items-center gap-1">
                    <IdCard size={11} /> Driver's License
                  </p>
                  <p className="text-xs font-mono truncate">{app.driver_license_number}</p>
                  {app.driver_license_image_url && (
                    <a
                      href={absDoc(app.driver_license_image_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-1"
                    >
                      <img
                        src={absDoc(app.driver_license_image_url)}
                        alt="Driver's license"
                        className="rounded border max-h-24 w-full object-cover hover:opacity-90"
                        data-testid={`ep-app-dl-image-${app.id}`}
                      />
                    </a>
                  )}
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-2">
                  <p className="label-eyebrow text-emerald-800 flex items-center gap-1">
                    <ShieldCheck size={11} /> Insurance
                  </p>
                  <p className="text-xs font-mono truncate">{app.insurance_policy_number}</p>
                  {app.insurance_image_url && (
                    <a
                      href={absDoc(app.insurance_image_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-1"
                    >
                      <img
                        src={absDoc(app.insurance_image_url)}
                        alt="Insurance"
                        className="rounded border max-h-24 w-full object-cover hover:opacity-90"
                        data-testid={`ep-app-ins-image-${app.id}`}
                      />
                    </a>
                  )}
                </div>
              </div>

              {app.status === "rejected" && app.rejection_reason && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">
                  <b>Rejection reason:</b> {app.rejection_reason}
                </p>
              )}
              {app.status === "approved" && (
                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-3">
                  Approved · DP profile created ({(app.partner_id || "").slice(0, 8)}…)
                </p>
              )}

              {app.status === "pending" && rejectingId !== app.id && (
                <div className="flex gap-2">
                  <Button
                    className="btn-brand flex-1"
                    onClick={() => approve(app)}
                    disabled={busyId === app.id}
                    data-testid={`ep-app-approve-${app.id}`}
                  >
                    <CheckCircle2 size={14} className="mr-1" />
                    {busyId === app.id ? "Approving…" : "Approve"}
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 border-red-200"
                    onClick={() => openReject(app)}
                    disabled={busyId === app.id}
                    data-testid={`ep-app-reject-${app.id}`}
                  >
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              )}

              {app.status === "pending" && rejectingId === app.id && (
                <div className="space-y-2 border-t pt-3">
                  <p className="text-xs text-gray-600">Reason (emailed to the applicant, optional)</p>
                  <Input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. License expired · resubmit with a current copy"
                    data-testid={`ep-app-reject-reason-${app.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 border-red-200 flex-1"
                      onClick={() => confirmReject(app)}
                      disabled={busyId === app.id}
                      data-testid={`ep-app-reject-confirm-${app.id}`}
                    >
                      {busyId === app.id ? "Rejecting…" : "Confirm reject"}
                    </Button>
                    <Button variant="ghost" onClick={cancelReject} data-testid={`ep-app-reject-cancel-${app.id}`}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div></>
      )}
      <BulkActions section="ep_applications" records={apps} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />
    </div>
  );
}
